import { useCallback } from 'react';
import Select from 'react-select';

import { useHasPermission } from '@/auth';
import { TextArea } from '@/components/UI/TextArea/TextArea';
import { defectTypeNietInzetbaar } from '@/domain/defecten/defect';
import usePlankTableStore from '@/views/ddp/hooks/usePlankTableStore';
import usePostNotitie from '@/views/ddp/hooks/usePostNotitie';
import useSidepanelActions from '@/views/ddp/hooks/useSidepanelActions';
import { DDPStatus } from '@/views/ddp/types/ddpTypes';
import { ddpStatusLabel } from '@/views/ddp/utils/sidepanel';

import notitieStyles from '../../Notitie/Notitie.module.css';
import tabStyles from './Tabs.module.css';

const valueLabelHelper = (value: string, label: string) => ({
  value,
  label: (
    <div data-testid={`react-select-${String(value)}`} className={notitieStyles.notitieStatusCircleStyleDiv}>
      <div className={notitieStyles[`notitieStatusCircleStyle-circle-${value}`]} />
      <div>{label}</div>
    </div>
  ),
});

const getStatusOptions = (isDefectNietInzetbaar = false) =>
  Object.entries(ddpStatusLabel)
    .filter((entry) => (entry[0] === 'NOTITIE_OP_SB_OB' && isDefectNietInzetbaar) || entry[0] !== 'NOTITIE_OP_SB_OB')
    .map((entry) => valueLabelHelper(entry[0], entry[1]));

/** Sidepanel tab notitie en status.
 *
 * @returns ReactNode
 */
const TabNotitie = () => {
  const sidepanel = usePlankTableStore((state) => state.sidepanel);
  const notitie = usePlankTableStore((state) => state.sidepanel.notitie);
  const isAllowedToDDPMutatie = useHasPermission('ddp.mutate');
  const { updateIsBusy, updateNotitieStatus, updateNotitieOpmerking, moveDefectToOnderhoud, moveDefectFromOnderhoud } =
    useSidepanelActions();

  const { putNotitie } = usePostNotitie();

  const opmerkingOnBlur = useCallback(() => {
    if (!isAllowedToDDPMutatie) return;
    const opmerking = notitie.opmerking?.trim();
    if (opmerking === undefined) {
      return;
    }
    (async () => {
      const response = await putNotitie({ ...notitie, opmerking });
      if (response !== null) {
        updateIsBusy(false);
      }
    })();
  }, [notitie, isAllowedToDDPMutatie]);

  const onChangeStatus = useCallback(
    (value, actionMeta) => {
      if (!isAllowedToDDPMutatie) return;
      const prevStatus = notitie.status;
      if (
        value === null ||
        actionMeta?.action !== 'select-option' ||
        value?.value === prevStatus ||
        (value?.value === '' && prevStatus === null)
      )
        return;

      const newStatus = value.value === '' ? null : (value.value as DDPStatus);
      updateNotitieStatus(newStatus);
      (async () => {
        const response = await putNotitie({ ...notitie, status: newStatus }, 'post-ddp-notitie-status');
        if (sidepanel.view !== 'defect' || !sidepanel.defect || response !== 'SUCCES') return;
        if (value.value === 'NOTITIE_OP_SB_OB') moveDefectToOnderhoud(sidepanel.defect);
        else if (prevStatus === 'NOTITIE_OP_SB_OB' && value.value !== 'NOTITIE_OP_SB_OB')
          moveDefectFromOnderhoud(sidepanel.defect);
      })();
    },
    [sidepanel, notitie, isAllowedToDDPMutatie],
  );
  return (
    <div className={tabStyles.spNotitieStyleDiv}>
      <div className={tabStyles.spNotitieStyleInputStatusDiv}>
        <label htmlFor='status' className={tabStyles.spNotitieStyleHeader}>
          Status
        </label>
        <Select
          isDisabled={!isAllowedToDDPMutatie}
          className='react-select-container'
          classNamePrefix='react-select'
          placeholder='Selecteer een status'
          inputId='status'
          name='status'
          value={getStatusOptions(sidepanel.view === 'defect' && defectTypeNietInzetbaar(sidepanel.defect)).filter(
            (opt) => opt.value === notitie.status,
          )}
          onChange={onChangeStatus}
          options={getStatusOptions(sidepanel.view === 'defect' && defectTypeNietInzetbaar(sidepanel.defect))}
        />
      </div>
      <div>
        <label htmlFor='opmerking' className={tabStyles.spNotitieStyleHeader}>
          Notitie ONB
        </label>
        <TextArea
          disabled={!isAllowedToDDPMutatie}
          id='opmerking'
          rows={5}
          value={sidepanel.notitie.opmerking ?? ''}
          onChange={(event) => {
            if (!isAllowedToDDPMutatie) return;
            updateNotitieOpmerking(event.currentTarget.value);
            updateIsBusy(true);
          }}
          onBlur={opmerkingOnBlur}
        />
      </div>
    </div>
  );
};
export default TabNotitie;
